import '../App.css';
import './Home.css';
import { Header } from '../components/header';
import { NoAi } from '../components/NoAi.js';
import AboutUs from '../components/AboutUs.js';
import Games from '../components/Games.js';
import { DiscordInvite } from '../components/DiscordInvite.js';
import Footer from '../components/Footer';
import Roadmap from '../components/Roadmap';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { useRef, useEffect } from 'react';

function Home() {
  useEffect(() => {
    document.title = "RedCoris - Indie Game Studio | Echoes of Sin: Confusion";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Experience a psychological descent into madness. RedCoris is an independent game studio creating immersive experiences like Echoes of Sin: Confusion.");
    }
  }, []);

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Mouse parallax effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    let { left, top, width, height } = currentTarget.getBoundingClientRect();
    let xPoint = (clientX - left) / width;
    let yPoint = (clientY - top) / height;

    mouseX.set(xPoint);
    mouseY.set(yPoint);
  }

  const titleX = useSpring(useTransform(mouseX, [0, 1], [15, -15]), { stiffness: 150, damping: 15 });
  const titleY = useSpring(useTransform(mouseY, [0, 1], [15, -15]), { stiffness: 150, damping: 15 });

  return (
    <div className="App">
      <section
        className="hero-section modern-hero"
        id="hero"
        ref={ref}
        onMouseMove={handleMouseMove}
      >
        <motion.div
          className="hero-background-layer"
          style={{ y: backgroundY }}
        />

        <Header />

        <main className="main-content hero-content-wrapper">
          <div className="hero-main-layout">
            <motion.div
              className="hero-info"
              style={{ y: textY, opacity }}
            >
              <motion.div
                className="hero-badge"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="pulsing-dot"></span> Out Now
              </motion.div>

              <motion.div
                className="hero-title-wrapper"
                style={{ x: titleX, y: titleY }}
              >
                <motion.h1
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <span className="first-line">ECHOES</span>
                  <span className="second-line">OF SIN</span>
                </motion.h1>
                <motion.h2
                  className="hero-subtitle glitch-text"
                  data-text="CONFUSION"
                  initial={{ opacity: 0, letterSpacing: "0.2em" }}
                  animate={{ opacity: 1, letterSpacing: "0.5em" }}
                  transition={{ delay: 0.5, duration: 1 }}
                >
                  CONFUSION
                </motion.h2>
              </motion.div>

              <motion.p
                className="hero-description"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                Experience a psychological descent into madness.
                <br />Where reality fractures and every shadow hides a secret.
              </motion.p>

              <motion.div
                className="hero-steam-widget"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
              >
                <iframe
                  src="https://store.steampowered.com/widget/4426280/"
                  frameBorder="0"
                  width="100%"
                  height="190"
                  title="Steam Wishlist Widget"
                ></iframe>
              </motion.div>
            </motion.div>

            <motion.div
              className="hero-media"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <div className="video-container">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/3AVl_6ODA0A?autoplay=0&mute=0&controls=1&rel=0"
                  title="Echoes of Sin: Confusion Release Trailer"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-glow"></div>
              </div>
            </motion.div>
          </div>

          <NoAi />
        </main>

        <div className="hero-scroll-indicator">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
          <div className="arrow-scroll">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </section>

      <Games />
      <AboutUs />
      <Roadmap />
      <DiscordInvite />
      <Footer />
    </div>
  );
}

export default Home;
